import { Stack, useTheme } from '@fluentui/react';
import { HubConnectionState } from '@microsoft/signalr';
import AppContext from 'App/AppContext';
import {
  IPDefaultButton,
  IPIconButton,
  IPPrimaryButton,
  IPSpinner,
  IPSpinnerSize,
  IPText,
  IPToggle,
} from 'baseComponents';
import { IPStack } from 'baseComponents/Stack/IPStack';
import DialogConfirmDelete from 'components/Dialogs/DialogConfirmDelete';
import DialogYesNo from 'components/Dialogs/DialogYesNo';
import AutoSaveStatusLabel from 'components/Notification/AutoSaveStatusLabel';
import ScrollableStackItem from 'components/Utils/ScrollableStackItem';
import { cancelIcon, globalStackTokensGapSmall } from 'globalStyles';
import { CopilotConversation, CopilotConversationContentBlock } from 'models/copilot/copilotConversation';
import { CopilotDocumentSelection } from 'models/copilot/copilotDocumentSelection';
import { AIEnabled } from 'models/setting';
import { DocumentEditMode, DocumentEditModeAI, DocumentEditModeUser } from 'models/userSetting';
import { lazy, ReactNode, Suspense, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiGetLink } from 'services/Api/linkService';
import { apiRequest, GraphScopeId } from 'services/Auth/authConfig';
import { graphUpdateFile } from 'services/Graph/graphServiceDrive';
import { CopilotAgentEventKind, NotifyDocumentState } from 'services/SignalR/CopilotHub';
import AppError from 'utils/appError';
import { arrayBufferToBase64 } from 'utils/array';
import { toLocaleTime } from 'utils/datetime';
import SharePointPageEditor, { SharePointPageEditorHandle } from '../../../SharePoint/PageEditor/SharePointPageEditor';
import { CopilotAgentChat } from '../../Agent/CopilotAgentChat';
import { CopilotAgentHeader } from '../../Agent/CopilotAgentHeader';
import { CopilotAgentSessionSeed, useCopilotAgentSession } from '../../Agent/useCopilotAgentSession';
import { CopilotApprovalDetailsModal } from '../Dialogs/CopilotApprovalDetailsModal';
import { DocumentMimeTypes, getDocumentViewMode } from './copilotDocumentBlockHelpers';
import { CopilotDocxEditorHandle } from './copilotDocxEditorHandle';

const CopilotDocxEditorSD = lazy(() => import('./CopilotDocxEditorSD'));

// Debounce window for autosaving user edits: persist 1.5s after the last change so typing is not saved per key.
const AUTOSAVE_DEBOUNCE_MS = 1500;
// A save that fails can reject within a few milliseconds, which would show the "saving" indicator for a
// single frame and leave the header looking permanently stuck on "save failed": a user who keeps editing
// cannot tell that their changes are still being retried. Hold the indicator long enough to be read.
const MIN_SAVING_INDICATOR_MS = 700;
const DOCX_EDITOR_SURFACE_Z_INDEX = 999990; // The docx editor surface is a fixed overlay that sits below the FLuent Layer (1000000) 
const DOCX_EDITOR_TOOLTIP_Z_INDEX = DOCX_EDITOR_SURFACE_Z_INDEX + 1;
const docxEditorTooltipCalloutProps = {
  // The tooltip Callout renders inside a Fluent Layer portaled to <body>
  layerProps: {
    styles: {
      root: {
        zIndex: DOCX_EDITOR_TOOLTIP_Z_INDEX,
      },
    },
  },
};

type DocxSaveStatus = 'idle' | 'saving' | 'saved' | 'error';

// Outcome of a single persist attempt: 'idle' = nothing to save (no-op), 'saved' = persisted, 'error' = a
// save was attempted and failed (error carries a human-readable reason for the status indicator).
// `warning` marks a save that DID persist the document but left something degraded (the session snapshot
// could not be refreshed). It stays a 'saved' outcome on purpose: the document is stored, so closing and
// the "has saved changes" bookkeeping must not be blocked by it.
interface SaveOutcome {
  status: 'idle' | 'saved' | 'error';
  error?: string;
  warning?: string;
}

export type CopilotDocumentEditStatus = 'running' | 'saving' | 'succeeded' | 'error' | 'closed';

export interface CopilotDocumentEditStatusChange {
  status: CopilotDocumentEditStatus;
  error?: string;
  hasSavedChanges?: boolean;
}

export interface CopilotDocumentEditModalProps {
  isOpen: boolean;
  // lastSaveError: the reason of the failed save the user chose to leave behind; undefined when the last save held
  onClose: (hasSavedChanges: boolean, lastSaveError?: string) => void;
  // The document block being edited (carries id, mimeType, savedLibraryLinkId, version, title).
  block: CopilotConversationContentBlock;
  // Context key used to scope the agent session (defaults to Generic when omitted).
  contextKey?: string;
  // Existing session to continue (handed off from the side panel). Omit to start a new session.
  session?: CopilotAgentSessionSeed;
  // Optional first instruction to submit after a library-editor handoff opens this modal.
  pendingInstruction?: string;
  // Optional one-shot reference to the instruction that runs with the pending instruction.
  // The backend resolves the instruction from the database and runs its text; only the reference is sent.
  instructionExternalId?: string;
  // Called on close so the caller (e.g. the side panel) can adopt edits made in the modal.
  onSessionUpdated?: (conversation: CopilotConversation | undefined, sessionId: number | undefined) => void;
  // Optional live status callback for callers that start the modal programmatically.
  onEditStatusChanged?: (change: CopilotDocumentEditStatusChange) => void;
}

interface IFullscreenDocxEditorSurfaceProps {
  title: string;
  // Optional node shown next to the title (e.g. the autosave status indicator).
  titleStatus?: ReactNode;
  // Optional controls shown on the right of the header, before the close button (e.g. edit-mode toggles).
  headerActions?: ReactNode;
  children: ReactNode;
  onClose: () => void;
}

export const DocxEditorSurface = (props: IFullscreenDocxEditorSurfaceProps): JSX.Element => {
  const { t } = useTranslation(['translation']);
  const theme = useTheme();

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: DOCX_EDITOR_SURFACE_Z_INDEX,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'rgba(0, 0, 0, 0.45)',
      }}
    >
      <div
        style={{
          width: '90vw',
          height: '90vh',
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
          padding: 20,
          border: `1px solid ${theme.semanticColors.bodyDivider}`,
          borderRadius: 2,
          boxShadow: theme.effects.elevation64,
          background: theme.semanticColors.bodyBackground,
          color: theme.semanticColors.bodyText,
        }}
      >
        <div
          style={{
            flex: '0 0 auto',
            display: 'flex',
            alignItems: 'flex-start',
            columnGap: 16,
            paddingBottom: 20,
            marginBottom: 20,
            borderBottom: `1px solid ${theme.semanticColors.bodyDivider}`,
          }}
        >
          {/* The header stays on exactly one line: nothing wraps, and the document title is the only part that
              gives up width (it truncates with an ellipsis). The close button sits outside this region so it
              stays pinned to the top-right. */}
          <div
            style={{
              display: 'flex',
              flexWrap: 'nowrap',
              alignItems: 'center',
              columnGap: 16,
              flex: '1 1 auto',
              minWidth: 0,
            }}
          >
            {/* Shrinkable: this group absorbs every width shortage on behalf of the whole header. */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0, flex: '0 1 auto' }}>
              <IPText
                variant="xLarge"
                nowrap
                block
                title={props.title}
                styles={{
                  root: {
                    color: theme.semanticColors.bodyText,
                    fontWeight: 600,
                    minWidth: 0,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  },
                }}
              >
                {props.title}
              </IPText>
              {/* The save status is short and must stay readable, so it never shrinks with the title. */}
              <div style={{ display: 'flex', alignItems: 'center', flex: '0 0 auto' }}>{props.titleStatus}</div>
            </div>
            <div
              style={{
                display: 'flex',
                flexWrap: 'nowrap',
                alignItems: 'center',
                columnGap: 16,
                flex: '0 0 auto',
                marginLeft: 'auto',
              }}
            >
              {props.headerActions}
            </div>
          </div>
          <IPIconButton
            iconProps={cancelIcon}
            title={t('translation:General.Button.Close')}
            onClick={props.onClose}
            styles={{ root: { flex: '0 0 auto' } }}
          />
        </div>
        <div style={{ flex: '1 1 auto', minHeight: 0, overflow: 'hidden' }}>{props.children}</div>
      </div>
    </div>
  );
};

// Large reusable document editing surface: the editor (docx / SharePoint page) on the left and the full
// Copilot agent chat on the right. The agent drives edits through the SignalR tool bridge, which is wired
// by handing the session's live hub connection to the left editor. On completion the edited document is
// persisted back into the session content block via NotifyDocumentState.
export const CopilotDocumentEditModal = (props: CopilotDocumentEditModalProps): JSX.Element | null => {
  const { t } = useTranslation(['copilot', 'translation']);
  const theme = useTheme();
  const appContext = useContext(AppContext);
  const {
    block,
    contextKey,
    isOpen,
    onClose,
    onEditStatusChanged,
    onSessionUpdated,
    pendingInstruction,
    instructionExternalId,
    session: initialSession,
  } = props;
  const mode = getDocumentViewMode(block);
  const showAgentChat = (appContext.globalDataCache.settings.get(AIEnabled) as boolean) === true;
  // Whether the docx edit-mode toggles apply: the docx editor honours user/agent tracked changes.
  const supportsEditModes = mode === 'docx';

  // Two independent, separately-persisted edit modes: one for the user's own typing, one for the agent's
  // edits. Seeded from the user's settings (default 'direct'); each toggle updates the cache and persists.
  const [userEditMode, setUserEditMode] = useState<DocumentEditMode>(
    () => (appContext.globalDataCache.userSettings.get(DocumentEditModeUser) as DocumentEditMode) ?? 'direct',
  );
  const [aiEditMode, setAiEditMode] = useState<DocumentEditMode>(
    () => (appContext.globalDataCache.userSettings.get(DocumentEditModeAI) as DocumentEditMode) ?? 'direct',
  );

  const updateEditMode = useCallback(
    (settingName: string, mode: DocumentEditMode, setState: (mode: DocumentEditMode) => void) => {
      setState(mode);
      void appContext.globalDataCache.userSettings.save(settingName, mode);
    },
    [appContext.globalDataCache.userSettings],
  );

  const docxEditorRef = useRef<CopilotDocxEditorHandle | null>(null);
  const pageEditorRef = useRef<SharePointPageEditorHandle | null>(null);
  const documentStateNotifiedRunRef = useRef<string | undefined>(undefined);
  // Cached SharePoint drive coordinates for a library-backed docx, resolved once from the library link.
  const libraryDriveRef = useRef<{ linkId: number; driveId: string; driveItemId: string; altTenantId?: string }>();

  const [saveStatus, setSaveStatus] = useState<DocxSaveStatus>('idle');
  // Human-readable reason shown (via the info tooltip) when saveStatus is 'error'.
  const [saveError, setSaveError] = useState<string | undefined>(undefined);
  // When the failing attempt ran. Shown next to the error so a user who missed the (brief) saving indicator
  // can still see that each edit is retried, rather than reading the error as a frozen dead end.
  const [saveErrorAt, setSaveErrorAt] = useState<Date | undefined>(undefined);
  // Set when the document was saved but something around it stayed degraded (see SaveOutcome.warning). Shown
  // as a non-blocking note on the 'saved' indicator; never suppresses the save itself.
  const [saveWarning, setSaveWarning] = useState<string | undefined>(undefined);
  // Set while an accept-all / reject-all action runs, so the buttons cannot be pressed twice. The result itself
  // is reported as a notification, not in the header.
  const [isDecidingTrackedChanges, setIsDecidingTrackedChanges] = useState<boolean>(false);
  // Set when the user tried to close while the document could not be saved, so the confirmation is shown.
  const [isConfirmingClose, setIsConfirmingClose] = useState<boolean>(false);
  // Debounce timer for user-edit autosave.
  const saveTimerRef = useRef<number | undefined>(undefined);
  // Guards against overlapping saves: while one is in flight, a later change sets pendingSaveRef so a single
  // follow-up save runs afterwards instead of racing.
  const isSavingRef = useRef<boolean>(false);
  const pendingSaveRef = useRef<boolean>(false);
  const saveLoopPromiseRef = useRef<Promise<SaveOutcome> | undefined>(undefined);
  const isClosingRef = useRef<boolean>(false);
  const hasSavedChangesRef = useRef<boolean>(false);
  const submittedPendingInstructionRef = useRef<string | undefined>(undefined);
  const pendingInstructionAwaitingCompletionRef = useRef<boolean>(false);

  // Live text selection from the editor, shown as a dismissable chip and attached to each Copilot request.
  // Only non-empty selections update it; an empty selection (e.g. the editor blurring when the user clicks
  // into the chat input) is ignored so the highlight survives. Cleared only when the user dismisses the chip
  // or selects new text.
  const [currentSelection, setCurrentSelection] = useState<CopilotDocumentSelection | null>(null);
  // Mirror in a ref so the session's descriptor builder can read the latest selection without re-creating.
  const currentSelectionRef = useRef<CopilotDocumentSelection | null>(null);
  currentSelectionRef.current = currentSelection;

  const handleSelectionChange = useCallback((selection: CopilotDocumentSelection | null) => {
    if (selection) setCurrentSelection(selection);
  }, []);

  const clearSelection = useCallback(() => setCurrentSelection(null), []);

  const session = useCopilotAgentSession({
    contextKey: contextKey ?? 'Generic',
    initialSession,
    selectedDocumentBlock: block,
    aiEditMode,
    getSelection: () => currentSelectionRef.current,
    instructionExternalId,
  });

  const {
    conversation,
    copilotSessionId,
    streamState,
    isStreaming,
    isLoading,
    hubConnection,
    getCopilotHubConnection,
    pendingDeleteApproval,
    setPendingDeleteApproval,
    approvalDetailsOperations,
    setApprovalDetailsOperations,
    onNewQuestion,
    onEditLastQuestion,
    onStop,
    onEntitySelection,
    onAdaptiveInputsChanged,
    onAdaptiveSubmit,
    onApprovalDetails,
    submitTaskApproval,
  } = session;

  // Resolve a hub connection to save through. The session owns a single persistent connection (opened when the
  // editor mounts and reused across runs), so saving never depends on the user having chatted and never opens a
  // second connection.
  const getSaveConnection = useCallback(async () => {
    if (hubConnection && hubConnection.state === HubConnectionState.Connected) return hubConnection;

    const opened = await getCopilotHubConnection();

    return opened?.connection;
  }, [hubConnection, getCopilotHubConnection]);

  // Persist the document/page state into the session content block over SignalR. Used for session-temp docx
  // (writes the binary to temp storage) and SharePoint pages (stores the concatenated web part HTML).
  const notifyDocumentState = useCallback(
    async (blockId: string, payload: string, contentType: string): Promise<SaveOutcome> => {
      if (!copilotSessionId) return { status: 'idle' };

      try {
        const connection = await getSaveConnection();
        if (!connection) return { status: 'idle' };

        await connection.invoke(NotifyDocumentState, copilotSessionId, blockId, payload, contentType);

        return { status: 'saved' };
      } catch (err) {
        // The edit stays visible in the editor but was NOT persisted to the session: surface it.
        return { status: 'error', error: AppError.fromApiError(err).message };
      }
    },
    [copilotSessionId, getSaveConnection],
  );

  // Write the edited .docx straight to the SharePoint library file via Graph (in-place content update, which
  // creates a new SharePoint version). The drive coordinates are resolved once from the library link and
  // cached. Mirrors how the SharePoint page editor writes through the user's delegated Graph token.
  const uploadDocxToLibrary = useCallback(
    async (linkId: number, buffer: ArrayBuffer): Promise<SaveOutcome> => {
      try {
        let info = libraryDriveRef.current;
        if (!info || info.linkId !== linkId) {
          const accessToken = await appContext.getAccessToken(apiRequest.scopes);
          const link = await apiGetLink(linkId, accessToken, appContext.globalDataCache);
          const driveId = link?.list?.spDriveId;
          const driveItemId = link?.driveItemId;
          if (!driveId || !driveItemId)
            return { status: 'error', error: t('copilot:Chat.Document.AutoSaveFailedDetail') };

          info = { linkId, driveId, driveItemId, altTenantId: link?.list?.altTenantId };
          libraryDriveRef.current = info;
        }

        const graphInterface = await appContext.getGraphInterface(GraphScopeId.Library, info.altTenantId);
        const file = new File([buffer], `${block?.title ?? 'document'}.docx`, { type: DocumentMimeTypes.docx });
        const result = await graphUpdateFile(graphInterface.client, file, info.driveId, info.driveItemId);

        return result
          ? { status: 'saved' }
          : { status: 'error', error: t('copilot:Chat.Document.AutoSaveFailedDetail') };
      } catch (err) {
        // The edit stays in the editor but was NOT written to the library file: surface it.
        return { status: 'error', error: AppError.fromApiError(err).message };
      }
    },
    [appContext, block?.title, t],
  );

  // Save the live document. Returns the outcome so the caller can drive the status indicator: 'idle' when
  // there is nothing to save yet, 'saved' on success, 'error' (with a reason) when a save was attempted and failed.
  const persistDocumentState = useCallback(async (): Promise<SaveOutcome> => {
    const blockId = block?.id;
    if (!blockId || !copilotSessionId) return { status: 'idle' };

    if (mode === 'docx') {
      // getDocumentContent returns null only when the editor holds no document yet, and throws when it
      // holds one but the export failed. Those mean different things to the user: the first is idle, the
      // second is an attempted save that did not reach the library file, so it has to be surfaced.
      let buffer: ArrayBuffer | null = null;
      try {
        buffer = (await docxEditorRef.current?.getDocumentContent()) ?? null;
      } catch {
        return { status: 'error', error: t('copilot:Chat.Document.AutoSaveFailedDetail') };
      }

      if (!buffer) return { status: 'idle' };

      // Library-backed docs live in SharePoint: write the edited binary straight to the drive item. Other
      // docx (uploaded/session-temp) have no library link and persist to session temp storage instead.
      if (block?.savedLibraryLinkId) {
        const librarySave = await uploadDocxToLibrary(block.savedLibraryLinkId, buffer);
        if (librarySave.status !== 'saved') return librarySave;

        // The SharePoint file now holds the edit, but the content block still carries the markdown snapshot
        // taken at ingest time. That snapshot is what the agent reads as the document, so leaving it stale
        // makes the next turn reason about (and potentially rewrite) the pre-edit text. Refresh it from the
        // same binary: the backend re-extracts the markdown into the block and never writes SharePoint, so
        // the library file stays the single source of truth for the document itself.
        const snapshot = await notifyDocumentState(blockId, arrayBufferToBase64(buffer), DocumentMimeTypes.docx);

        // The SharePoint write already succeeded, so this stays a 'saved' outcome whatever the refresh did:
        // reporting an error would block the close handler and clear "has saved changes" for a document that
        // is safely stored, trapping the user in the editor (and re-uploading a new SharePoint version on
        // every close attempt). Any refresh that did not complete leaves the snapshot equally stale, whether
        // it failed ('error') or could not run at all ('idle', e.g. no hub connection), so both surface the
        // same non-blocking warning.
        return snapshot.status !== 'saved'
          ? { status: 'saved', warning: t('copilot:Chat.Document.SnapshotRefreshFailedDetail') }
          : librarySave;
      }

      return notifyDocumentState(blockId, arrayBufferToBase64(buffer), DocumentMimeTypes.docx);
    }

    if (mode === 'page') {
      // Flush web parts edited (and buffered) during the agent run to SharePoint in a single pass, then store
      // the concatenated HTML in the session content block. Surface a flush failure instead of masking it.
      const flush = await pageEditorRef.current?.flushPendingEdits();
      if (flush && !flush.ok) return { status: 'error', error: flush.error };

      const html = pageEditorRef.current?.getPageHtml() ?? '';
      if (!html) return { status: 'idle' };

      return notifyDocumentState(blockId, html, DocumentMimeTypes.sharePointPage);
    }

    return { status: 'idle' };
  }, [block?.id, block?.savedLibraryLinkId, copilotSessionId, mode, t, uploadDocxToLibrary, notifyDocumentState]);

  // Run a save now and surface its status. Coalesces overlapping requests: a change that lands while a save
  // is in flight queues exactly one more save afterwards so the latest edit is never dropped.
  const runSave = useCallback(async (): Promise<SaveOutcome> => {
    if (isSavingRef.current) {
      pendingSaveRef.current = true;

      return saveLoopPromiseRef.current ?? { status: 'idle' };
    }

    isSavingRef.current = true;
    const saveLoop = (async (): Promise<SaveOutcome> => {
      onEditStatusChanged?.({ status: 'saving' });
      setSaveStatus('saving');

      let outcome: SaveOutcome = { status: 'idle' };
      try {
        do {
          pendingSaveRef.current = false;
          const startedAt = Date.now();
          outcome = await persistDocumentState();

          // Deliberately inside the loop: isSavingRef stays held for the whole wait, so an edit that lands
          // during it still sets pendingSaveRef and is picked up by the condition below instead of being
          // dropped, and no later save can have its outcome overwritten by this one.
          const elapsed = Date.now() - startedAt;
          if (elapsed < MIN_SAVING_INDICATOR_MS) {
            await new Promise((resolve) => window.setTimeout(resolve, MIN_SAVING_INDICATOR_MS - elapsed));
          }
        } while (pendingSaveRef.current);
      } catch (err) {
        outcome = { status: 'error', error: AppError.fromApiError(err).message };
      } finally {
        isSavingRef.current = false;
      }

      if (outcome.status === 'saved') hasSavedChangesRef.current = true;

      setSaveStatus(outcome.status);
      setSaveError(outcome.status === 'error' ? outcome.error : undefined);
      setSaveErrorAt(outcome.status === 'error' ? new Date() : undefined);
      setSaveWarning(outcome.status === 'saved' ? outcome.warning : undefined);

      return outcome;
    })();

    saveLoopPromiseRef.current = saveLoop;
    try {
      return await saveLoop;
    } finally {
      saveLoopPromiseRef.current = undefined;
    }
  }, [onEditStatusChanged, persistDocumentState]);

  // Latest runSave, so the unmount cleanup can flush a pending debounced edit without depending on a stale
  // closure.
  const runSaveRef = useRef(runSave);
  runSaveRef.current = runSave;

  // SuperDoc content change (user or agent edit). While the agent is working, skip per-edit autosave -- the
  // run-completion effect below saves once when the agent is done. Otherwise debounce the user's edits and
  // only flip to "saving" once the debounce fires, so a single keystroke does not immediately show a spinner.
  const handleContentChange = useCallback(() => {
    if (isStreaming) return;

    if (saveTimerRef.current !== undefined) window.clearTimeout(saveTimerRef.current);
    saveTimerRef.current = window.setTimeout(() => {
      saveTimerRef.current = undefined;
      void runSave();
    }, AUTOSAVE_DEBOUNCE_MS);
  }, [isStreaming, runSave]);

  // On unmount, flush a still-pending debounced edit so a close that bypasses handleClose (e.g. the parent
  // clearing the editor target) does not silently drop it. Best-effort, fire-and-forget. The session owns the
  // hub connection and stops it itself.
  useEffect(
    () => () => {
      if (saveTimerRef.current !== undefined) {
        window.clearTimeout(saveTimerRef.current);
        saveTimerRef.current = undefined;
        void runSaveRef.current();
      }
    },
    [],
  );

  // When an agent run completes, save the document once (its edits supersede any queued per-edit autosave).
  useEffect(() => {
    if (!streamState.isDone) {
      documentStateNotifiedRunRef.current = undefined;

      return;
    }

    const runId = streamState.runId ?? 'run';
    if (documentStateNotifiedRunRef.current === runId) return;

    const errorEvent = streamState.events.find((event) => event.kind === CopilotAgentEventKind.Error);
    if (errorEvent) {
      if (pendingInstructionAwaitingCompletionRef.current) {
        pendingInstructionAwaitingCompletionRef.current = false;
        onEditStatusChanged?.({ status: 'error', error: errorEvent.text });
      }

      return;
    }

    if (!block?.id || !copilotSessionId) return;
    if (mode !== 'docx' && mode !== 'page') return;

    documentStateNotifiedRunRef.current = runId;
    const isPendingInstructionRun = pendingInstructionAwaitingCompletionRef.current;

    if (saveTimerRef.current !== undefined) {
      window.clearTimeout(saveTimerRef.current);
      saveTimerRef.current = undefined;
    }

    // Escalate the outcome of THIS save (the one the completed pending-instruction run triggered) rather than
    // reading a shared in-progress flag, so a concurrent user autosave can never be mis-reported as the run's
    // result.
    void runSave().then((outcome) => {
      if (!isPendingInstructionRun) return;

      pendingInstructionAwaitingCompletionRef.current = false;
      if (outcome.status === 'saved') onEditStatusChanged?.({ status: 'succeeded' });
      else if (outcome.status === 'error') onEditStatusChanged?.({ status: 'error', error: outcome.error });
    });
  }, [
    streamState.isDone,
    streamState.runId,
    streamState.events,
    copilotSessionId,
    block?.id,
    mode,
    runSave,
    onEditStatusChanged,
  ]);

  useEffect(() => {
    const trimmedPendingInstruction = pendingInstruction?.trim();
    if (!trimmedPendingInstruction || !isOpen || !block?.id || !copilotSessionId) return;
    if (isStreaming || isLoading || hubConnection?.state !== HubConnectionState.Connected) return;

    const instructionKey = `${copilotSessionId}:${block.id}:${trimmedPendingInstruction}`;
    if (submittedPendingInstructionRef.current === instructionKey) return;

    submittedPendingInstructionRef.current = instructionKey;
    pendingInstructionAwaitingCompletionRef.current = true;
    onEditStatusChanged?.({ status: 'running' });
    void onNewQuestion(trimmedPendingInstruction);
  }, [
    pendingInstruction,
    isOpen,
    block?.id,
    copilotSessionId,
    hubConnection?.state,
    isStreaming,
    isLoading,
    onNewQuestion,
    onEditStatusChanged,
  ]);

  // The close sequence without the save. Also reached from the confirmation dialog, when the user decides to
  // leave a document whose save keeps failing.
  const finishClose = useCallback(
    (lastSaveError?: string) => {
      const hasSavedChanges = hasSavedChangesRef.current;
      hasSavedChangesRef.current = false;
      onSessionUpdated?.(conversation, copilotSessionId);
      onEditStatusChanged?.({ status: hasSavedChanges ? 'succeeded' : 'closed', hasSavedChanges });
      onClose(hasSavedChanges, lastSaveError);
    },
    [conversation, copilotSessionId, onClose, onEditStatusChanged, onSessionUpdated],
  );

  const handleClose = async () => {
    if (isClosingRef.current) return;

    isClosingRef.current = true;
    try {
      let closeSaveOutcome: SaveOutcome | undefined;
      if (saveTimerRef.current !== undefined) {
        window.clearTimeout(saveTimerRef.current);
        saveTimerRef.current = undefined;
        closeSaveOutcome = await runSave();
      } else if (saveLoopPromiseRef.current) {
        closeSaveOutcome = await saveLoopPromiseRef.current;
      } else if (saveStatus === 'error') {
        closeSaveOutcome = await runSave();
      }

      if (closeSaveOutcome?.status === 'error') {
        onEditStatusChanged?.({ status: 'error', error: closeSaveOutcome.error });
        // Never refuse the close outright: a document the editor cannot export (e.g. a .docx missing a
        // required package part) fails every attempt, so refusing would leave the close button dead for as
        // long as the modal is open. Let the user decide whether to leave the unsaved changes behind.
        setIsConfirmingClose(true);

        return;
      }

      finishClose();
    } finally {
      isClosingRef.current = false;
    }
  };

  // Accept-all / reject-all can partly succeed (SuperDoc refuses revisions it cannot plan, such as structural
  // row-level table changes), so always report what happened rather than assuming the whole batch went through.
  const runTrackChangeDecision = async (decision: 'accept' | 'reject') => {
    const editor = docxEditorRef.current;
    if (!editor) return;

    const decide = decision === 'accept' ? editor.acceptAllTrackedChanges : editor.rejectAllTrackedChanges;

    setIsDecidingTrackedChanges(true);
    try {
      const outcome = await decide();
      if (outcome.applied === 0 && outcome.failed === 0) {
        appContext.showNotification(t('copilot:Chat.Document.EditMode.NoTrackedChanges'));
      } else if (outcome.failed === 0) {
        // Everything went through; the document itself is the feedback.
      } else if (outcome.applied === 0) {
        appContext.showNotification(
          decision === 'accept'
            ? t('copilot:Chat.Document.EditMode.AcceptAllFailed')
            : t('copilot:Chat.Document.EditMode.RejectAllFailed'),
          true,
        );
      } else {
        appContext.showNotification(
          t('copilot:Chat.Document.EditMode.DecisionPartial', {
            applied: outcome.applied,
            failed: outcome.failed,
          }),
          false,
          true,
        );
      }
    } catch {
      // The editor bridge already reports its own failures as an outcome and logs the cause, so anything landing
      // here is unexpected. Still report it: the click handler discards this promise, so without a catch the user
      // gets a dead button and an unhandled rejection.
      appContext.showNotification(
        decision === 'accept'
          ? t('copilot:Chat.Document.EditMode.AcceptAllFailed')
          : t('copilot:Chat.Document.EditMode.RejectAllFailed'),
        true,
      );
    } finally {
      setIsDecidingTrackedChanges(false);
    }
  };

  if (!isOpen) return null;

  const title = block.title ?? t('copilot:Chat.Document.Untitled');

  // The failure carries the time of the attempt so a user who missed the (brief) saving indicator can still
  // see that each edit is retried, rather than reading the error as a frozen dead end.
  const saveErrorText = saveErrorAt
    ? t('copilot:Chat.Document.AutoSaveFailedAt', { time: toLocaleTime(saveErrorAt) })
    : t('copilot:Chat.Document.AutoSaveFailed');

  const saveStatusNode = (
    <AutoSaveStatusLabel
      status={saveStatus}
      savingText={t('copilot:Chat.Document.AutoSaving')}
      savedText={t('copilot:Chat.Document.AutoSaved')}
      errorText={saveErrorText}
      errorDetail={saveError || t('copilot:Chat.Document.AutoSaveFailedDetail')}
      warning={saveWarning}
      tooltipCalloutProps={docxEditorTooltipCalloutProps}
    />
  );

  const editModeToggles = supportsEditModes ? (
    <div style={{ display: 'flex', flexWrap: 'nowrap', alignItems: 'center', columnGap: 12 }}>
      {/* The two edit-mode switches read as one cluster: a soft rounded surface groups them and sets them
          apart from the affirmative Accept / Reject actions that follow. */}
      <div
        style={{
          display: 'flex',
          flexWrap: 'nowrap',
          alignItems: 'center',
          columnGap: 18,
          padding: '5px 14px',
          borderRadius: 999,
          background: theme.semanticColors.bodyStandoutBackground,
          border: `1px solid ${theme.semanticColors.bodyDivider}`,
        }}
      >
        <IPToggle
          inlineLabel
          label={t('copilot:Chat.Document.EditMode.UserLabel')}
          onText={t('copilot:Chat.Document.EditMode.Tracked')}
          offText={t('copilot:Chat.Document.EditMode.Direct')}
          checked={userEditMode === 'tracked'}
          onChange={(_ev, checked) =>
            updateEditMode(DocumentEditModeUser, checked ? 'tracked' : 'direct', setUserEditMode)
          }
          styles={{ root: { marginBottom: 0 } }}
        />
        {showAgentChat && (
          <IPToggle
            inlineLabel
            label={t('copilot:Chat.Document.EditMode.AiLabel')}
            onText={t('copilot:Chat.Document.EditMode.Tracked')}
            offText={t('copilot:Chat.Document.EditMode.Direct')}
            checked={aiEditMode === 'tracked'}
            onChange={(_ev, checked) =>
              updateEditMode(DocumentEditModeAI, checked ? 'tracked' : 'direct', setAiEditMode)
            }
            styles={{ root: { marginBottom: 0 } }}
          />
        )}
      </div>
      <div style={{ display: 'flex', flexWrap: 'nowrap', alignItems: 'center', columnGap: 8 }}>
        <IPPrimaryButton
          iconProps={{ iconName: 'CheckMark' }}
          text={t('copilot:Chat.Document.EditMode.AcceptAll')}
          title={t('copilot:Chat.Document.EditMode.AcceptAllTooltip')}
          disabled={isDecidingTrackedChanges}
          onClick={() => void runTrackChangeDecision('accept')}
        />
        <IPDefaultButton
          iconProps={{ iconName: 'Cancel' }}
          text={t('copilot:Chat.Document.EditMode.RejectAll')}
          title={t('copilot:Chat.Document.EditMode.RejectAllTooltip')}
          disabled={isDecidingTrackedChanges}
          onClick={() => void runTrackChangeDecision('reject')}
        />
        {isDecidingTrackedChanges && <IPSpinner size={IPSpinnerSize.small} />}
      </div>
    </div>
  ) : undefined;

  // A bare <IPSpinner> centers only horizontally and sits at the top of its content box. Wrap the lazy-chunk
  // Suspense fallback in a full-height, both-axes-centered stack so this first loading phase matches the
  // editor's own centered loading spinner (DocxEditorSD's isLoading state) instead of jumping from top to center.
  const editorLoadingFallback = (
    <IPStack verticalFill verticalAlign="center" horizontalAlign="center">
      <IPSpinner label={t('copilot:Chat.Document.EditorLoading')} />
    </IPStack>
  );

  return (
    <>
      <DocxEditorSurface
        title={title}
        titleStatus={saveStatusNode}
        headerActions={editModeToggles}
        onClose={handleClose}
      >
        <IPStack horizontal styles={{ root: { height: '100%', minHeight: 0, overflow: 'hidden', columnGap: '12px' } }}>
          <Stack verticalFill styles={{ root: { width: showAgentChat ? 'calc(80% - 6px)' : '100%', minWidth: 300 } }}>
            <ScrollableStackItem>
              {mode === 'docx' && (
                <Suspense fallback={editorLoadingFallback}>
                  <CopilotDocxEditorSD
                    ref={docxEditorRef}
                    sessionId={copilotSessionId}
                    blockId={block.id}
                    hubConnection={hubConnection}
                    onContentChange={handleContentChange}
                    onSelectionChange={handleSelectionChange}
                    userEditMode={userEditMode}
                    aiEditMode={aiEditMode}
                  />
                </Suspense>
              )}
              {mode === 'page' && (
                <SharePointPageEditor
                  ref={pageEditorRef}
                  block={block}
                  hubConnection={hubConnection}
                  sessionId={copilotSessionId}
                  isAgentWorking={isStreaming}
                  onSelectionChange={handleSelectionChange}
                  onSaveStateChange={(status, errorMessage) => {
                    if (status === 'saved') hasSavedChangesRef.current = true;

                    setSaveStatus(status);
                    setSaveError(status === 'error' ? errorMessage : undefined);
                    setSaveErrorAt(status === 'error' ? new Date() : undefined);
                    setSaveWarning(undefined);
                    // Only the generic saving indicator here. The pending-instruction succeeded/error status is
                    // escalated from the run-completion runSave outcome so a plain user save is not mis-reported.
                    if (status === 'saving') onEditStatusChanged?.({ status: 'saving' });
                  }}
                />
              )}
            </ScrollableStackItem>
          </Stack>
          {showAgentChat && (
            <IPStack.Item styles={{ root: { width: 'calc(20% - 6px)', minWidth: 300 } }}>
              <IPStack verticalFill tokens={globalStackTokensGapSmall} styles={{ root: { minHeight: 0, height: '100%' } }}>
                {/* the same header as every embedded assistant: the assistant's icon and name, with what it does here */}
                <IPStack.Item>
                  <CopilotAgentHeader subtitle={t('copilot:Chat.Document.AssistantSubtitle')} />
                </IPStack.Item>
                <IPStack.Item grow styles={{ root: { minHeight: 0, display: 'flex' } }}>
                  <CopilotAgentChat
                    conversation={conversation}
                    sessionId={copilotSessionId}
                    isStreaming={isStreaming}
                    isLoading={isLoading}
                    // Mirror the side panel's general-knowledge agent: lock the chat input while the agent is
                    // working so the user cannot queue a question mid-run, and so the "working" placeholder shows.
                    chatDisabled={isStreaming || isLoading}
                    streamState={streamState}
                    welcomeMessage={t('copilot:Panel.WelcomeMessage')}
                    onNewQuestion={onNewQuestion}
                    onStop={onStop}
                    onEntitySelection={onEntitySelection}
                    onAdaptiveInputsChanged={onAdaptiveInputsChanged}
                    onAdaptiveSubmit={onAdaptiveSubmit}
                    onApprovalDetails={onApprovalDetails}
                    onEditLastQuestion={onEditLastQuestion}
                    selection={mode === 'docx' || mode === 'page' ? currentSelection : null}
                    onClearSelection={clearSelection}
                  />
                </IPStack.Item>
              </IPStack>
            </IPStack.Item>
          )}
        </IPStack>
      </DocxEditorSurface>
      {/* Outside the agent-chat block on purpose: the editor can be closed with the chat hidden too. */}
      <DialogYesNo
        hidden={!isConfirmingClose}
        title={t('copilot:Chat.Document.CloseUnsaved.Title')}
        subText={t('copilot:Chat.Document.CloseUnsaved.SubText')}
        onYes={() => {
          setIsConfirmingClose(false);
          finishClose(saveError || t('copilot:Chat.Document.AutoSaveFailedDetail'));
        }}
        onNo={() => setIsConfirmingClose(false)}
      />
      {showAgentChat && (
        <>
          <DialogConfirmDelete
            hidden={pendingDeleteApproval === null}
            title={t('copilot:Panel.DeleteConfirm.Title')}
            subText={t('copilot:Panel.DeleteConfirm.SubText')}
            onYes={() => {
              const pending = pendingDeleteApproval;
              setPendingDeleteApproval(null);
              if (pending) void submitTaskApproval(pending.itemId, pending.block, pending.payload);
            }}
            onNo={() => setPendingDeleteApproval(null)}
          />
          <CopilotApprovalDetailsModal
            isOpen={approvalDetailsOperations !== null}
            operations={approvalDetailsOperations ?? []}
            onClose={() => setApprovalDetailsOperations(null)}
          />
        </>
      )}
    </>
  );
};

export default CopilotDocumentEditModal;
